export type OrderStatus =
  | 'In Progress'
  | 'Pending'
  | 'Cancelled'
  | 'Accepted'
  | 'Completed'

export type PaymentStatus = 'Unpaid' | 'Partial' | 'Settled'

export type LabOrderListItem = {
  id: string
  _id: string
  case: string
  labName: string
  clinic: string
  orderStatus: OrderStatus
  payment: PaymentStatus
  orderDate?: string
  dueDate?: string
  finalTotal: string
  totalReceived?: string
  balanceDue?: string
  balanceDueAmount?: number
  itemCount?: number
  patient?: string
  patientMeta?: string
  treatmentId?: string
  isLocalTracking?: boolean
  trackingMode?: 'platform' | 'local'
}

export type InvoiceLineItem = {
  item: string
  description?: string
  notes?: string
  price: number
  lineDiscount: number
  netPrice: number
}

export type PaymentRecord = {
  date: string
  method: string
  amount: number
  discount: number
  total: number
  recordedBy: string
}

export type WorkflowStep = {
  title: string
  timestamp?: string
  estimate?: string
  state: 'completed' | 'current' | 'upcoming'
}

export type StageLogEntry = {
  date: string
  stage: string
  status: 'Completed' | 'In Progress' | 'Pending'
  by: string
  addedBy: string
  notes: string
}

export type LabOrderDetail = LabOrderListItem & {
  clinicLocation: string
  patient: string
  patientMeta: string
  caseTitle: string
  caseMeta: string
  caseDetail: string
  toothNos: string[]
  receivedBy: string
  receivedType: string
  deliveryAddress: string
  contact: string
  addedBy: string
  paymentLabel: string
  netTotal: number
  generalDiscount: number
  deliveryCharge: number
  finalTotalAmount: number
  totalReceived: number
  balanceDue: number
  invoiceItems: InvoiceLineItem[]
  payments: PaymentRecord[]
  workflow: WorkflowStep[]
  stageLog: StageLogEntry[]
}

export const orderStatusStyles: Record<OrderStatus, string> = {
  Pending: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  'In Progress': 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  Accepted: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  Completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  Cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
}

export const paymentStatusStyles: Record<PaymentStatus, string> = {
  Unpaid: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  Partial: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  Settled: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
}

export const stageStatusStyles: Record<StageLogEntry['status'], string> = {
  Completed: 'bg-emerald-100 text-emerald-800',
  'In Progress': 'bg-amber-100 text-amber-800',
  Pending: 'bg-slate-100 text-slate-700',
}

export function formatLkr(amount: number) {
  return `LKR ${(Number(amount) || 0).toLocaleString('en-LK')}`
}

export function resolveOrderKey(order: { _id?: string; id?: string }) {
  const mongoId = order._id != null ? String(order._id).trim() : ''
  if (mongoId) return mongoId
  return String(order.id || '').trim()
}

export function orderMatchesKey(
  order: { _id?: string; id?: string } | null | undefined,
  key: string | null | undefined,
) {
  if (!order || key == null) return false
  const normalizedKey = String(key).trim()
  if (!normalizedKey) return false
  const mongoId = order._id != null ? String(order._id).trim() : ''
  const orderNumber = order.id != null ? String(order.id).trim() : ''
  return mongoId === normalizedKey || orderNumber === normalizedKey
}
